// Browser-compatible logger that works in React apps
// Falls back to console in browser, can use winston in Node.js environments

const isDevelopment = import.meta.env.MODE === 'development';

// Log levels
const LOG_LEVELS = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3
};

// Allow environment variable override: REACT_APP_LOG_LEVEL=debug|info|warn|error
const getLogLevel = () => {
  const envLevel = import.meta.env.VITE_LOG_LEVEL;
  if (envLevel && LOG_LEVELS[envLevel] !== undefined) {
    return LOG_LEVELS[envLevel];
  }
  return isDevelopment ? LOG_LEVELS.debug : LOG_LEVELS.info;
};

const currentLogLevel = getLogLevel();

// Browser logger implementation
const createBrowserLogger = () => {
  const formatMessage = (level, message, meta) => {
    const now = new Date();
    const timestamp = now.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
    const metaString = meta && Object.keys(meta).length ? ` ${JSON.stringify(meta)}` : '';
    return [`${timestamp} [${level.toUpperCase()}]`, message, metaString].filter(Boolean);
  };

  const shouldLog = (level) => LOG_LEVELS[level] >= currentLogLevel;

  return {
    debug: (message, ...meta) => {
      if (shouldLog('debug')) {
        console.debug(...formatMessage('debug', message, meta[0]));
      }
    },
    info: (message, ...meta) => {
      if (shouldLog('info')) {
        console.info(...formatMessage('info', message, meta[0]));
      }
    },
    warn: (message, ...meta) => {
      if (shouldLog('warn')) {
        console.warn(...formatMessage('warn', message, meta[0]));
      }
    },
    error: (message, ...meta) => {
      if (shouldLog('error')) {
        console.error(...formatMessage('error', message, meta[0]));
      }
    }
  };
};

// Create logger instance
const logger = createBrowserLogger();

// Helper functions to match console API
export const log = {
  debug: (message, ...meta) => logger.debug(message, ...meta),
  info: (message, ...meta) => logger.info(message, ...meta),
  warn: (message, ...meta) => logger.warn(message, ...meta),
  error: (message, ...meta) => logger.error(message, ...meta),
  
  // Convenience methods
  log: (message, ...meta) => logger.info(message, ...meta), // Map console.log to info
};

// Add context helpers for common patterns
export const createContextLogger = (context) => ({
  debug: (message, ...meta) => logger.debug(message, { context, ...meta }),
  info: (message, ...meta) => logger.info(message, { context, ...meta }),
  warn: (message, ...meta) => logger.warn(message, { context, ...meta }),
  error: (message, ...meta) => logger.error(message, { context, ...meta }),
  log: (message, ...meta) => logger.info(message, { context, ...meta }),
});

export default logger;